/**
 * Icon-only button.
 *
 * The prototype used bare `<Pencil onClick=... style={{cursor:"pointer"}} />` SVGs
 * for the song card's row of actions. Those were not focusable, not keyboard
 * operable, and unnamed to a screen reader — five unlabelled click targets. This
 * renders a real button with an accessible name and identical visuals: no
 * background, no border, no padding, so the icon sits exactly where it did.
 */
import type { CSSProperties, ReactNode } from 'react';
import { DISABLED_OPACITY } from '../../styles/theme';

export interface IconButtonProps {
  /** The icon element. */
  children: ReactNode;
  onClick?: () => void;
  /** Required: this is the button's only accessible name. */
  label: string;
  /** Stops the click from reaching a clickable ancestor, e.g. a song card. */
  stopPropagation?: boolean;
  /**
   * For icon buttons that are toggles: renders aria-pressed.
   *
   * Omitted means "not a toggle" — an unconditional aria-pressed={false} would
   * announce every plain action button as an unpressed toggle.
   */
  pressed?: boolean;
  style?: CSSProperties;
  disabled?: boolean;
  /**
   * Renders the browser's own tooltip. On by default, because for most of these it
   * is the only hint there is.
   *
   * Turn it OFF when the button is wrapped in `<Tooltip>`: the two are independent
   * mechanisms, so both fire and the styled bubble appears with the UA's grey box
   * arriving a second later underneath it. The Tooltip is the better one of the pair
   * — it also works on focus and on touch, which `title` does not — so it wins, and
   * the accessible name is unaffected either way because that comes from
   * `aria-label`.
   */
  nativeTitle?: boolean;
}

/**
 * Whether this button is left at the size of its glyph.
 *
 * THE PROBLEM THIS ANSWERS. About half the `IconButton`s in the app pass an explicit
 * `width`/`height` — 44px in the full-screen player, 32 in the queue panel, 30 on a library
 * row, 28 in the mini player — and the other half pass none, which with the component's
 * `padding: 0` means the hit area IS the icon: 13px in the Studio's lyrics header, 15px on a
 * Vault pack card, 14px on the upload panel's remove. On a phone that is a target a third the
 * width of a fingertip, sitting next to another one.
 *
 * The floor is applied only to the unsized ones, through `.lyx-icon-button` and one rule in
 * the existing coarse-pointer block. It has to be conditional: a CSS `min-width` would BEAT
 * an inline `width`, so an unconditional class would grow every sized button too — including
 * the mini player's, which is approved and must not be redesigned.
 *
 * Reads `width`/`height` only. A caller that sizes a button by padding is already stating a
 * hit area, and one that sets `minWidth` is stating a floor of its own; neither is this.
 */
function hasExplicitSize(style: CSSProperties | undefined): boolean {
  return style?.width !== undefined || style?.height !== undefined;
}

export default function IconButton({
  children,
  onClick,
  label,
  stopPropagation,
  pressed,
  style,
  disabled,
  nativeTitle = true,
}: IconButtonProps) {
  return (
    <button
      type="button"
      // Only when the caller has not sized it — see hasExplicitSize. Every other visual
      // property stays exactly where it was; the class carries nothing but the touch floor.
      className={hasExplicitSize(style) ? undefined : 'lyx-icon-button'}
      aria-label={label}
      title={nativeTitle ? label : undefined}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        onClick?.();
      }}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        // Shared with Button so a disabled icon and a disabled label fade
        // together — see styles/theme.ts. A glyph has no text beside it to fall
        // back on, so fading it out of visibility loses the control entirely.
        opacity: disabled ? DISABLED_OPACITY : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
}
